/**
 * AR-380 — the one editing model, the one status vocabulary, the one set of
 * scope labels. Every card on every page of the Account center goes through
 * here, which is what makes "one editing model across every card" checkable
 * rather than a matter of taste.
 */

import type { AccountRecord, AccountRole } from './accountFixture';

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

/** A slice of the record, and optionally which of its fields a card owns. */
export interface AccountCardSpec {
  /** The record slice this card edits. */
  slice: keyof AccountRecord;
  /**
   * The fields of that slice the card saves. Omit when the card owns the whole
   * slice. Two cards on one slice share the draft copy and each saves only its
   * own fields, so neither can save the other's work.
   */
  fields?: string[];
  /**
   * A second slice the card edits alongside its own — the public contact card
   * carries the social links, which live on `pub`.
   */
  also?: Partial<Record<keyof AccountRecord, string[]>>;
}

/** Which slice each card edits. The key is the card's id, used by every handler. */
export const ACCOUNT_CARDS = {
  you: { slice: 'you' },
  addr: { slice: 'addr' },
  // v15 split the business in two. `biz` is Your Business — the entity's own
  // facts. The legal entity name is not among them: changing it needs a member
  // agreement, so the form shows it and cannot save it.
  biz: { slice: 'biz', fields: ['type', 'since', 'work'] },
  // `brand` moved to Brand + Public Presence with the name, tagline and
  // description the public actually reads (D-ACCT-30, D-ACCT-32).
  brand: { slice: 'biz', fields: ['brand', 'tagline', 'desc'] },
  pub: { slice: 'pub' },
  spec: { slice: 'spec' },
  cta: { slice: 'cta', also: { pub: ['social'] } },
  teamset: { slice: 'teamset', fields: ['crm', 'vault'] },
  teamdef: { slice: 'teamset', fields: ['brand', 'terms'] },
  crm: { slice: 'crm', fields: ['taskDays', 'stallDays'] },
  propc: { slice: 'prop', fields: ['welcome', 'closing'] },
  propt: { slice: 'prop', fields: ['termsMode', 'termsLink', 'termsText'] },
  propx: { slice: 'prop', fields: ['hidePrices', 'cta'] },
  vault: { slice: 'vault', fields: ['payEmail'] },
  web: { slice: 'web', fields: ['mode', 'url'] },
  voice: { slice: 'voice', fields: ['tone', 'style', 'notes'] },
  // Two cards, one slice: the billing address is editable on Personal +
  // Security as well as on the card it belongs to on Billing.
  bill: { slice: 'bill', fields: ['same', 'street', 'city', 'state', 'zip'] },
  billaddr: { slice: 'bill', fields: ['same', 'street', 'city', 'state', 'zip'] },
} as const satisfies Record<string, AccountCardSpec>;

export type AccountCardKey = keyof typeof ACCOUNT_CARDS;

/** The three cards whose Save makes a public draft rather than a live change. */
export const ACCOUNT_PUBLIC_CARDS: readonly AccountCardKey[] = ['pub', 'spec', 'cta'];

export const isPublicCard = (k: AccountCardKey): boolean =>
  ACCOUNT_PUBLIC_CARDS.includes(k);

/** The slices a card edits: its own, plus any it borrows a field from. */
export function accountCardSlices(k: AccountCardKey): (keyof AccountRecord)[] {
  const spec = ACCOUNT_CARDS[k] as AccountCardSpec;
  return [spec.slice, ...(Object.keys(spec.also ?? {}) as (keyof AccountRecord)[])];
}

// ---------------------------------------------------------------------------
// One status vocabulary (a word outside this set is a bug)
// ---------------------------------------------------------------------------

export type AccountStatusTone = 'green' | 'amber' | 'grey' | 'blue';

export const ACCOUNT_STATUS: Record<string, AccountStatusTone> = {
  Complete: 'green',
  Incomplete: 'amber',
  Connected: 'green',
  'Not connected': 'grey',
  Published: 'green',
  Draft: 'amber',
  'Expiring soon': 'amber',
  'Action required': 'amber',
  'Managed by team': 'grey',
  Current: 'green',
  'On file': 'green',
  'Not on file': 'amber',
  'Not set up': 'grey',
  Live: 'green',
  // AR-391 — `user_website_accounts.publish_state = unpublished`: a site that
  // was live and has been taken down, which is neither Draft nor Deactivated.
  Unpublished: 'grey',
  'Not yet': 'grey',
  Required: 'grey',
  Set: 'green',
  Paid: 'green',
  Active: 'green',
  Preparing: 'blue',
  'Not requested': 'grey',
  'Not kept yet': 'grey',
  On: 'green',
  Off: 'grey',
  Signed: 'green',
  Expired: 'amber',
  Deactivated: 'grey',
};

export const accountStatusTone = (word: string): AccountStatusTone =>
  ACCOUNT_STATUS[word] ?? 'grey';

// ---------------------------------------------------------------------------
// One set of scope labels — whose setting a card's values are
// ---------------------------------------------------------------------------

export type AccountScope = 'personal' | 'business' | 'team' | 'public' | 'owner';

export const ACCOUNT_SCOPE_LABELS: Record<AccountScope, string> = {
  personal: 'Personal setting',
  business: 'Entire business',
  team: 'Team default',
  public: 'Public',
  owner: 'Managed by account owner',
};

// ---------------------------------------------------------------------------
// Visibility rules
// ---------------------------------------------------------------------------

export const isAccountOwner = (role: AccountRole): boolean => role === 'Owner';

/**
 * Team + Access is for the people who manage a team. An Associate Advisor is
 * not one of them, so the row drops rather than showing them the owner's
 * data-access rules.
 */
export const showsTeamPage = (role: AccountRole): boolean =>
  role === 'Owner' || role === 'Manager';

/**
 * Business + Brand: the legal entity name and business type are not tracked at
 * associate level, so an Associate Advisor's card carries neither row, in the
 * view or the form.
 */
export const showsLegalEntity = (role: AccountRole): boolean =>
  role !== 'Associate Advisor';

// ---------------------------------------------------------------------------
// Taxonomies and option lists
// ---------------------------------------------------------------------------

export const ACCOUNT_TIMEZONES = [
  'Eastern',
  'Central',
  'Mountain',
  'Pacific',
  'Alaska',
  'Hawaii',
];

/** The Business type row asks what the company IS, so the options are entity types. */
export const ACCOUNT_ENTITY_TYPES = [
  'Sole proprietor',
  'Limited Liability Company',
  'S corporation',
  'C corporation',
  'Partnership',
];

/** How the advisor works — PRO calls this My Current Time Allocation. */
export const ACCOUNT_WORK_STATUS = [
  'Full-time',
  'Part-time',
  'Transitioning to full-time',
  'Not currently selling',
  'Other',
];

export const ACCOUNT_PHONE_TYPES = ['Mobile', 'Office', 'Home', 'Other'];
export const ACCOUNT_SALUTATIONS = ['Mr.', 'Mrs.', 'Ms.', 'Mx.', 'Dr.'];
export const ACCOUNT_SOCIAL_TYPES = [
  'Instagram',
  'Facebook',
  'LinkedIn',
  'YouTube',
  'Website',
];

/**
 * The controlled taxonomies behind the three expertise fields. A typed value
 * the taxonomy does not carry is refused with a line under the field — adding
 * is always a second, explicit act.
 */
export const ACCOUNT_TAXONOMIES = {
  dest: [
    'Alaska', 'Amalfi Coast', 'Antarctica', 'Australia', 'Bahamas', 'Bali',
    'Caribbean', 'Costa Rica', 'Croatia', 'Egypt', 'Europe', 'France',
    'Galápagos', 'Greece', 'Hawaii', 'Iceland', 'Ireland', 'Italy', 'Japan',
    'Kenya', 'Maldives', 'Mexico', 'Morocco', 'New Zealand', 'Peru', 'Portugal',
    'Scotland', 'South Africa', 'Spain', 'Tahiti', 'Thailand', 'Turkey',
    'United Kingdom', 'Vietnam',
  ],
  interests: [
    'Adventure', 'Art and architecture', 'Beach', 'Celebrations',
    'Culture and history', 'Diving and snorkeling', 'Food and wine', 'Golf',
    'Hiking', 'Honeymoons', 'Multigenerational', 'Music and festivals',
    'Nature and wildlife', 'Photography', 'Shopping', 'Skiing',
    'Spa and wellness', 'Sports', 'Theme parks', 'Wine regions',
  ],
  styles: [
    'Accessible travel', 'Adults only', 'All-inclusive resorts', 'Cruises',
    'Destination weddings', 'Escorted tours', 'Expedition cruises',
    'Family vacations', 'Group travel', 'Independent travel', 'Luxury',
    'Rail journeys', 'River cruises', 'Road trips', 'Safaris',
    'Small-ship cruises', 'Solo travel', 'Villas and home rentals',
  ],
} as const;

/** Which taxonomy FENCES which field path — only a listed value is accepted. */
export const ACCOUNT_TAXONOMY_BY_PATH: Record<string, readonly string[]> = {
  'spec.dest': ACCOUNT_TAXONOMIES.dest,
  'spec.interests': ACCOUNT_TAXONOMIES.interests,
  'spec.styles': ACCOUNT_TAXONOMIES.styles,
};

/**
 * Awards + certifications autocompletes but does NOT fence: an advisor's own
 * award is still an award, and a curated list could never be complete
 * (D-ACCT-44). Every other tag field stays list-only.
 */
export const ACCOUNT_SUGGESTIONS_BY_PATH: Record<string, readonly string[]> = {
  'pub.awards': [
    'ACC — Accredited Cruise Counsellor',
    'MCC — Master Cruise Counsellor',
    'ECC — Elite Cruise Counsellor',
    'CLIA Certified Cruise Counsellor',
    'CTA — Certified Travel Associate',
    'CTC — Certified Travel Counselor',
    'CTIE — Certified Travel Industry Executive',
    'Sandals Certified Specialist',
    'Disney College of Knowledge',
    'Universal Partner Certified',
    'Virtuoso Advisor',
    'TLN Luxury specialist',
  ],
};

export const ACCOUNT_BUDGETS = [
  'Under $5,000',
  '$5,000 – $10,000',
  '$10,000 – $25,000',
  '$25,000 – $50,000',
  'Over $50,000',
];
export const ACCOUNT_DURATIONS = [
  'A weekend',
  '4–6 nights',
  '7–10 nights',
  '11–14 nights',
  'Over two weeks',
];
export const ACCOUNT_PARTY_SIZES = [
  '1 traveler',
  '2 travelers',
  '2–6 travelers',
  '6–10 travelers',
  'Groups over 10',
];
export const ACCOUNT_ACCESS_LEVELS = [
  'Private to each advisor',
  'Team can view',
  'Team can view and edit',
  'Managers and owners only',
];

export const ACCOUNT_WRITING_TONES = [
  'Professional',
  'Friendly',
  'Casual',
  'Enthusiastic',
  'Authoritative',
  'Conversational',
];
export const ACCOUNT_WRITING_STYLES = [
  'Direct and concise',
  'Detailed and comprehensive',
  'Storytelling',
  'Educational',
  'Inspirational',
];

export const ACCOUNT_SAMPLE_KINDS: readonly (readonly [string, string, string])[] = [
  ['Email', 'email', 'emails'],
  ['Blog post', 'blog post', 'blog posts'],
  ['Social post', 'social post', 'social posts'],
  ['Client proposal', 'client proposal', 'client proposals'],
  ['Itinerary', 'itinerary', 'itineraries'],
  ['Other', 'other piece', 'other pieces'],
];

/** The three roles the app ships, each with what it can do. */
export const ACCOUNT_ROLE_PERMISSIONS: readonly (readonly [
  AccountRole,
  string,
  readonly string[],
])[] = [
  [
    'Owner',
    "billing, plan, documents and everyone's roles",
    [
      'Billing, the plan and the payment method',
      'Agreements and the documents WorldVia keeps',
      'Team roles, defaults and data access',
      'Every client, trip and payment in the business',
    ],
  ],
  [
    'Manager',
    "all of the agency's clients, trips and money",
    [
      'Every client, trip and payment in the business',
      'Invite and deactivate members',
      'Team defaults',
      'Not billing and not the plan',
    ],
  ],
  [
    'Associate Advisor',
    'only their own clients and trips',
    [
      'Their own clients, trips and payments',
      'Their own public profile',
      'Their own preferences and AIVIA settings',
      "Not billing, the plan or another advisor's records",
    ],
  ],
];

export const ACCOUNT_ROLES: readonly AccountRole[] = ACCOUNT_ROLE_PERMISSIONS.map(
  ([r]) => r,
);

export const ACCOUNT_ASSIST_LEVELS: readonly (readonly [string, string])[] = [
  ['Minimal', 'AIVIA answers when asked and rarely offers a suggestion on its own.'],
  [
    'Balanced',
    'AIVIA answers when asked and suggests the next step or an improvement when it sees one. Recommended.',
  ],
  [
    'Proactive',
    'AIVIA looks for opportunities, proposes next steps and prepares drafts. It still asks before anything consequential.',
  ],
];

export const ACCOUNT_NOTIFICATION_ROWS: readonly (readonly [string, string])[] = [
  ['tasks', 'Task reminders'],
  ['proposals', 'Proposal activity'],
  ['payments', 'Payment activity'],
  ['team', 'Team activity'],
  ['product', 'Product announcements'],
];

/** Notification rows that cannot be turned off. */
export const ACCOUNT_NOTIFICATION_REQUIRED = [
  'Security and sign-in',
  'Billing',
  'Compliance',
];

export const ACCOUNT_CONTINUITY_HINT =
  'A document naming who takes over your open bookings and client files if you are unable to work. WorldVia asks Crown Club and Diamond Club members to keep one on file.';

// ---------------------------------------------------------------------------
// Small derivations the pages share. Each is computed, never typed, so a row
// and the value it describes cannot drift apart.
// ---------------------------------------------------------------------------

export const accountYears = (from: number, to: number): string[] => {
  const out: string[] = [];
  for (let y = to; y >= from; y--) out.push(String(y));
  return out;
};

export const accountCount = (n: number, one: string, many: string): string =>
  `${n} ${n === 1 ? one : many}`;

export const accountParagraphs = (t: string): string[] =>
  String(t ?? '')
    .split(/\n\s*\n/)
    .filter((x) => x.trim());

export const accountMoney = (v: number): string => `$${Number(v).toFixed(2)}`;

/** Read a value out of the record by dot path ("you.first"). */
export function accountGet(obj: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (x, k) => (x == null ? x : (x as Record<string, unknown>)[k]),
      obj,
    );
}

/** Write a value into a draft by dot path, mutating the object in place. */
export function accountPut(obj: unknown, path: string, value: unknown): void {
  const keys = path.split('.');
  const last = keys.pop() as string;
  const target = keys.reduce<Record<string, unknown>>(
    (x, k) => x[k] as Record<string, unknown>,
    obj as Record<string, unknown>,
  );
  target[last] = value;
}
